import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { PageInitialDataError, unwrapPageInitialData } from "~/app/api/pageData";
import { orderHistoryPageQueryOptions } from "~/app/api/pageData/orderHistoryPageQuery";
import type {
  OrderHistoryAccountData,
  OrderHistoryFeedbackOptionParentData,
  OrderHistoryPageInitialData,
} from "~/app/api/pageData/orderHistoryPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import {
  create as createOrderHistoryDisplayPage,
  type OrderHistoryPageProps,
} from "~/features/orders/orderList/OrderListPageComponent";
import type {
  FeedbackOption,
  FeedbackOptionCategory,
  UserData,
} from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/components/OrderListFeedbackContext";
import type {
  MultiAccountAccountData,
  MultiAccountEndCustomerData,
} from "~/features/orders/orderList/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSearchDataProvider";

const ORDER_HISTORY_PATH = "/orders";

function toFeedbackChildren(options: { label: string; value: string }[]): FeedbackOption[] {
  return options.map((option) => ({ label: option.label, value: option.value }));
}

function toFeedbackOptionCategories(options: OrderHistoryFeedbackOptionParentData[]): FeedbackOptionCategory[] {
  return options.map((option, index) => ({
    label: option.label,
    value: String(index),
    title: option.title,
    children: toFeedbackChildren(option.children),
  }));
}

function toEndCustomers(endCustomers: { code: string; name: string }[]): MultiAccountEndCustomerData[] {
  return endCustomers.map((endCustomer) => ({
    code: endCustomer.code,
    name: endCustomer.name,
  }));
}

function toAccounts(
  accounts: OrderHistoryAccountData[] | undefined,
): { accounts: MultiAccountAccountData[] } | undefined {
  if (!accounts) {
    return undefined;
  }
  return {
    accounts: accounts.map((account) => ({
      code: account.code,
      name: account.name ?? "",
      shortCode: account.shortCode,
      salesOrg: account.salesOrg,
      endCustomer: toEndCustomers(account.endCustomers),
    })),
  };
}

function toUserData(userData: Record<string, string>): UserData {
  return {
    uid: userData.uid ?? "",
    firstName: userData.firstName ?? "",
    lastName: userData.lastName ?? "",
    email: userData.email ?? "",
    phone: userData.phone ?? "",
  };
}

function toOrderHistoryProps(data: OrderHistoryPageInitialData): OrderHistoryPageProps {
  const props: OrderHistoryPageProps = {
    userData: toUserData(data.userData),
    orderFeedbackOptions: toFeedbackOptionCategories(data.feedbackOptions),
  };
  if (data.accounts) {
    props.accounts = toAccounts(data.accounts);
  }
  return props;
}

function OrderHistoryRoute() {
  const result = useLoaderData({
    from: ORDER_HISTORY_PATH,
  }) as Awaited<ReturnType<typeof loadOrderHistoryPageData>>;
  const shellProps = useStorefrontShellProps();

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createOrderHistoryDisplayPage(toOrderHistoryProps(result.data))}
    </StorefrontShellFrame>
  );
}

export async function loadOrderHistoryPageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(orderHistoryPageQueryOptions());
  const pageData = unwrapPageInitialData(result);

  if (!pageData.data) {
    throw new PageInitialDataError({
      code: "INVALID_ORDER_HISTORY_PAGE_INITIAL_DATA",
      message: "OK order history page initial data must include data.",
    });
  }

  return {
    data: pageData.data,
    metadata: pageData.metadata,
  };
}

export const orderHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ORDER_HISTORY_PATH,
  loader: async ({ context }) => loadOrderHistoryPageData(context),
  component: OrderHistoryRoute,
});
